/* @flow */

import * as React from 'react'
import chunk from 'lodash/chunk'

import webshopWidgets from '../WebshopWidgets'

const useWidgetTreeDataCache = () => {
  const [dataCache, setDataCache] = React.useState({})
  const [isInitialized, setIsInitialized] = React.useState(false)

  const registerWidgetTreeWidgetsToDataCache = React.useCallback(
    gridItems => {
      const dataCacheEntries = {}

      for (let gridItem of gridItems) {
        const GridItemType = webshopWidgets[gridItem.type]

        if (!GridItemType || !GridItemType.data_config) {
          continue
        }

        const dataConfig = GridItemType.data_config
        const key = dataConfig.key_creator(gridItem)

        dataCacheEntries[key] = {
          ...dataConfig,
          key,
          grid_item: gridItem,
        }
      }

      setDataCache(s => {
        const copy = { ...s }

        for (let [key, dataConfig] of Object.entries(dataCacheEntries)) {
          if (copy[key]) {
            continue
          }

          copy[key] = {
            ...dataConfig,
            isInitialized: false,
            isFetching: false,
            data: null,
            overrides: {},
          }
        }

        return copy
      })
    },
    [setDataCache]
  )

  const clearDataCache = React.useCallback(() => {
    setDataCache({})
  }, [setDataCache])

  const invalidateCacheKey = React.useCallback(
    gridItem => {
      const gridItemType = webshopWidgets[gridItem.type]

      if (gridItemType && gridItemType.data_config) {
        setDataCache(s => {
          const copy = { ...s }

          const key = gridItemType.data_config.key_creator(gridItem)

          if (copy[key]) {
            copy[key] = {
              ...copy[key],
            }

            copy[key].data = null
            copy[key].promise = null
            copy[key].isInitialized = false
            copy[key].overrides = {}
          }

          return copy
        })
      }
    },
    [setDataCache]
  )

  const initializeDataCache = React.useCallback(() => {
    setIsInitialized(true)
  }, [setIsInitialized])

  const removeOverrides = React.useCallback(
    (gridItem, keys) => {
      const gridItemType = webshopWidgets[gridItem.type]

      if (gridItemType && gridItemType.data_config) {
        setDataCache(s => {
          const copy = { ...s }

          const key = gridItemType.data_config.key_creator(gridItem)

          let updated = false

          if (copy[key]) {
            const updatedOverrides = {
              ...copy[key].overrides,
            }

            for (let key of keys) {
              if (key in updatedOverrides) {
                delete updatedOverrides[key]
                updated = true
              }
            }

            // trigger refresh
            copy[key].promise = null
            copy[key].overrides = updatedOverrides
          }

          if (!updated) {
            return s
          }

          return copy
        })
      }
    },
    [setDataCache]
  )

  const setOverrides = React.useCallback(
    (gridItem, overrides) => {
      const gridItemType = webshopWidgets[gridItem.type]

      if (gridItemType && gridItemType.data_config) {
        setDataCache(s => {
          const copy = { ...s }

          const key = gridItemType.data_config.key_creator(gridItem)

          if (copy[key]) {
            // trigger refresh
            copy[key].promise = null
            copy[key].overrides = {
              ...copy[key].overrides,
              ...overrides,
            }
          }

          return copy
        })
      }
    },
    [setDataCache]
  )

  React.useEffect(() => {
    if (!isInitialized) {
      return
    }

    const createPromisesByType = {}
    for (let [key, dataConfig] of Object.entries(dataCache)) {
      if (dataCache[key] && dataCache[key].promise) {
        continue
      }

      let groupKey = dataConfig.bulk ? dataConfig.grid_item.type : key

      if (!createPromisesByType[groupKey]) {
        createPromisesByType[groupKey] = {
          bulk: dataConfig.bulk,
          chunk: dataConfig.bulk ? dataConfig.bulk_chunk || 20 : 1,
          promises_to_make: [],
        }
      }

      createPromisesByType[groupKey].promises_to_make.push(dataConfig)
    }

    const createdPromisesByKey = {}
    for (let [groupKey, groupData] of Object.entries(createPromisesByType)) {
      const isBulk = groupData.bulk
      const chunks = chunk(groupData.promises_to_make, groupData.chunk)

      for (let chunk of chunks) {
        const promise = chunk[0]
          .create_promise(
            chunk.map(entry => ({
              grid_item: {
                ...entry.grid_item,
                data: {
                  ...entry.grid_item.data,
                  ...(entry.overrides || {}),
                },
              },
              key: entry.key,
            }))
          )
          .then(response => {
            setDataCache(s => {
              const copy = { ...s }

              if (!isBulk) {
                if (!response.error) {
                  copy[chunk[0].key].data = response.payload
                }

                copy[chunk[0].key].isInitialized = true
                copy[chunk[0].key].isFetching = false
              } else {
                for (let entry of response.payload) {
                  copy[entry.key].data = entry

                  copy[entry.key].isInitialized = true
                  copy[entry.key].isFetching = false
                }
              }

              return copy
            })
          })

        for (let chunkEntry of chunk) {
          createdPromisesByKey[chunkEntry.key] = promise
        }
      }
    }

    if (Object.keys(createdPromisesByKey).length > 0) {
      setDataCache(s => {
        const copy = { ...s }

        for (let [key, promise] of Object.entries(createdPromisesByKey)) {
          copy[key] = {
            ...copy[key],
            promise,
            isFetching: true,
          }
        }

        return copy
      })
    }
  }, [dataCache, isInitialized, setDataCache])

  return {
    dataCache,
    clearDataCache,
    registerWidgetTreeWidgetsToDataCache,
    initializeDataCache,
    invalidateCacheKey,
    removeOverrides,
    setOverrides,
  }
}

export { useWidgetTreeDataCache }
